import React, { useState, useEffect, useCallback } from 'react';
import './WordPuzzleGame.css';

interface WordPuzzle {
  word: string;
  scrambled: string;
  hint: string;
  category: string;
}

const WORD_PUZZLES: WordPuzzle[] = [
    // Beginner puzzles - Simple words for young children
    { word: 'APPLE', scrambled: 'PAPLE', hint: 'A red or green fruit', category: 'Fruits' },
    { word: 'BALL', scrambled: 'LLAB', hint: 'Round object you can throw', category: 'Toys' },
    { word: 'CAT', scrambled: 'TAC', hint: 'Furry pet that meows', category: 'Animals' },
    { word: 'DOG', scrambled: 'GOD', hint: 'Friendly pet that barks', category: 'Animals' },
    { word: 'FISH', scrambled: 'HSIF', hint: 'Swims in water', category: 'Animals' },
    { word: 'HAT', scrambled: 'TAH', hint: 'Wear on your head', category: 'Clothes' },
    { word: 'SUN', scrambled: 'NUS', hint: 'Bright light in sky', category: 'Nature' },
    { word: 'MOON', scrambled: 'NOOM', hint: 'Shines at night', category: 'Nature' },
    { word: 'STAR', scrambled: 'RATS', hint: 'Twinkles in sky', category: 'Nature' },
    { word: 'TREE', scrambled: 'EERT', hint: 'Tall plant with leaves', category: 'Nature' },
    { word: 'BIRD', scrambled: 'DRIB', hint: 'Flies in the sky', category: 'Animals' },
    { word: 'CAKE', scrambled: 'EKAC', hint: 'Sweet dessert', category: 'Food' },
    { word: 'MILK', scrambled: 'KLIM', hint: 'White drink', category: 'Food' },
    { word: 'BED', scrambled: 'DEB', hint: 'Where you sleep', category: 'Home' },
    { word: 'CAR', scrambled: 'RAC', hint: 'Vehicle with wheels', category: 'Transport' },
    { word: 'SHOE', scrambled: 'HOES', hint: 'Wear on feet', category: 'Clothes' },
    { word: 'BOOK', scrambled: 'KOOB', hint: 'Has pages to read', category: 'Education' },
    { word: 'TOY', scrambled: 'YOT', hint: 'Something to play with', category: 'Toys' },
    { word: 'CUP', scrambled: 'PUC', hint: 'Drink from this', category: 'Home' },
    { word: 'KEY', scrambled: 'YEK', hint: 'Opens doors', category: 'Home' },
    { word: 'BEAR', scrambled: 'RAEB', hint: 'Big furry animal', category: 'Animals' },
    { word: 'DUCK', scrambled: 'KCUD', hint: 'Swims in pond', category: 'Animals' },
    { word: 'FROG', scrambled: 'GORF', hint: 'Jumps and croaks', category: 'Animals' },
    { word: 'LION', scrambled: 'NOIL', hint: 'King of jungle', category: 'Animals' },
    { word: 'MOUSE', scrambled: 'ESUOM', hint: 'Small squeaky animal', category: 'Animals' },
    { word: 'PIG', scrambled: 'GIP', hint: 'Pink farm animal', category: 'Animals' },
    { word: 'COW', scrambled: 'WOC', hint: 'Gives milk', category: 'Animals' },
    { word: 'HEN', scrambled: 'NEH', hint: 'Lays eggs', category: 'Animals' },
    { word: 'BEE', scrambled: 'EEB', hint: 'Makes honey', category: 'Animals' },
    { word: 'ANT', scrambled: 'TNA', hint: 'Small hard worker', category: 'Animals' },
    
    // Easy puzzles
    { word: 'PUZZLE', scrambled: 'ZLEUPZ', hint: 'A brain teaser or problem to solve', category: 'Games' },
    { word: 'SUDOKU', scrambled: 'KUDOSU', hint: 'A number placement puzzle', category: 'Games' },
    { word: 'CHESS', scrambled: 'HESSC', hint: 'A strategic board game', category: 'Games' },
    { word: 'REWARD', scrambled: 'WAREDR', hint: 'Something given for achievement', category: 'General' },
    { word: 'BRAIN', scrambled: 'RABIN', hint: 'The organ that thinks', category: 'Body' },
    { word: 'LOGIC', scrambled: 'GILOC', hint: 'Reasoning and thinking', category: 'Mind' },
    { word: 'MEMORY', scrambled: 'MOREMY', hint: 'The ability to remember', category: 'Mind' },
    { word: 'PATTERN', scrambled: 'TARPENT', hint: 'A repeated design or sequence', category: 'General' },
    { word: 'STRATEGY', scrambled: 'TARSTEGY', hint: 'A plan to achieve a goal', category: 'Mind' },
    { word: 'CHALLENGE', scrambled: 'LENCHAGEL', hint: 'A difficult task or problem', category: 'General' }
];

const WordPuzzleGameComponent: React.FC = () => {
  const [currentPuzzle, setCurrentPuzzle] = useState<WordPuzzle | null>(null);
  const [selectedLetters, setSelectedLetters] = useState<number[]>([]);
  const [currentWord, setCurrentWord] = useState('');
  const [score, setScore] = useState(0);
  const [currentLevel, setCurrentLevel] = useState(1);
  const [gameWon, setGameWon] = useState(false);
  const [showHint, setShowHint] = useState(false);
  const [timeLeft, setTimeLeft] = useState(60);
  const [gameStarted, setGameStarted] = useState(false);

  const loadNewPuzzle = useCallback(() => {
    const puzzleIndex = (currentLevel - 1) % WORD_PUZZLES.length;
    const puzzle = WORD_PUZZLES[puzzleIndex];
    setCurrentPuzzle(puzzle);
    setSelectedLetters([]);
    setCurrentWord('');
    setShowHint(false);
  }, [currentLevel]);

  useEffect(() => {
    if (gameStarted && timeLeft > 0) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    } else if (timeLeft === 0) {
      setGameWon(true);
    }
  }, [timeLeft, gameStarted]);

  useEffect(() => {
    loadNewPuzzle();
  }, [loadNewPuzzle]);

  const handleLetterClick = (index: number) => {
    if (!currentPuzzle) return;
    
    const newSelectedLetters = [...selectedLetters];
    const letterIndex = newSelectedLetters.indexOf(index);
    
    if (letterIndex > -1) {
      // Remove letter from selection
      newSelectedLetters.splice(letterIndex, 1);
    } else {
      // Add letter to selection
      newSelectedLetters.push(index);
    }
    
    setSelectedLetters(newSelectedLetters);
    
    // Update current word
    const newWord = newSelectedLetters
      .map(i => currentPuzzle.scrambled[i])
      .join('');
    setCurrentWord(newWord);
  };

  const handleSubmit = () => {
    if (!currentPuzzle) return;

    if (currentWord === currentPuzzle.word) {
      setScore(prev => prev + (showHint ? 5 : 10)); // Bonus points for no hint
      setCurrentLevel(prev => prev + 1);
      setShowHint(false);
    } else {
      // Wrong answer - show hint and clear selection
      setShowHint(true);
      setSelectedLetters([]);
      setCurrentWord('');
    }
  };

  const handleClear = () => {
    setSelectedLetters([]);
    setCurrentWord('');
  };

  const startGame = () => {
    setGameStarted(true);
    setTimeLeft(60);
    setScore(0);
    setCurrentLevel(1);
    setGameWon(false);
    loadNewPuzzle();
  };

  const resetGame = () => {
    setGameStarted(false);
    setTimeLeft(60);
    setScore(0);
    setCurrentLevel(1);
    setGameWon(false);
    setSelectedLetters([]);
    setCurrentWord('');
    setShowHint(false);
  };

  const getPerformanceText = () => {
    if (score >= 50) return "Word Master! 🎉";
    if (score >= 30) return "Great Job! ⭐";
    if (score >= 20) return "Good Work! 👍";
    return "Keep Practicing! 💪";
  };

  return (
    <div className="word-puzzle-game">
      <div className="game-header">
        <h2>📝 Word Puzzle Game</h2>
        <p>Unscramble the letters to form the correct word!</p>
      </div>
      
      {!gameStarted ? (
        <div className="game-start">
          <div className="start-content">
            <h3>Ready to test your word skills?</h3>
            <p>You have 60 seconds to solve as many word puzzles as possible!</p>
            <button onClick={startGame} className="start-button">
              🚀 Start Game
            </button>
          </div>
        </div>
      ) : !gameWon ? (
        <>
          <div className="game-stats">
            <div className="stat-item">
              <span className="stat-label">Score:</span>
              <span className="stat-value">{score}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Level:</span>
              <span className="stat-value">{currentLevel}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Time:</span>
              <span className="stat-value">{timeLeft}s</span>
            </div>
          </div>

          {currentPuzzle && (
            <div className="puzzle-content">
              <div className="puzzle-info">
                <h3>Category: {currentPuzzle.category}</h3>
                
                {/* Current Word Display */}
                <div className="current-word-display">
                  <div className="word-display">
                    {currentWord || "Tap letters to form a word"}
                  </div>
                </div>
              </div>

              {/* Letter Grid (Touch-based) */}
              <div className="letter-grid">
                {currentPuzzle.scrambled.split('').map((letter, index) => {
                  const isSelected = selectedLetters.includes(index);
                  return (
                    <button
                      key={index}
                      className={`letter-button ${isSelected ? 'selected' : ''}`}
                      onClick={() => handleLetterClick(index)}
                    >
                      {letter}
                    </button>
                  );
                })}
              </div>

              {/* Action Buttons */}
              <div className="action-buttons">
                <button onClick={handleClear} className="clear-button">
                  Clear
                </button>
                <button 
                  onClick={handleSubmit} 
                  className="submit-button"
                  disabled={!currentWord}
                >
                  Submit
                </button>
              </div>

              {showHint && (
                <div className="hint-section">
                  <p><strong>💡 Hint:</strong> {currentPuzzle.hint}</p>
                </div>
              )}
            </div>
          )}
        </>
      ) : (
        <div className="game-end">
          <div className="end-content">
            <h3>🎉 Game Over!</h3>
            <div className="final-stats">
              <p><strong>Final Score:</strong> {score}</p>
              <p><strong>Levels Completed:</strong> {currentLevel - 1}</p>
              <p><strong>Performance:</strong> {getPerformanceText()}</p>
            </div>
            <div className="end-buttons">
              <button onClick={startGame} className="play-again-button">
                🔄 Play Again
              </button>
              <button onClick={resetGame} className="reset-button">
                🏠 Main Menu
              </button>
            </div>
          </div>
        </div>
      )}
      
      <div className="game-controls">
        {gameStarted && !gameWon && (
          <button onClick={resetGame} className="reset-button">
            🏠 Main Menu
          </button>
        )}
      </div>
      
      <div className="game-instructions">
        <h4>How to Play:</h4>
        <ul>
          <li>Tap letters to select them and form the correct word</li>
          <li>Selected letters will be highlighted in green</li>
          <li>Tap a letter again to deselect it</li>
          <li>Get 10 points for correct answers, 5 points if you used a hint</li>
          <li>You have 60 seconds to solve as many puzzles as possible</li>
        </ul>
      </div>
    </div>
  );
};

export default WordPuzzleGameComponent;
